<template>
  <div class="notification-toggle">
    <Button
      variant="icon"
      size="medium"
      :title="isOpen ? 'Close notifications' : 'Open notifications'"
      :aria-label="isOpen ? 'Close notifications' : 'Open notifications'"
      :aria-expanded="isOpen"
      aria-haspopup="true"
      class="notification-toggle__button"
      @click="togglePanel"
    >
      <div class="notification-toggle__icon-container">
        <Icon name="bell" />
        <Badge
          v-if="notificationCount > 0"
          :text="notificationCount > 99 ? '99+' : notificationCount.toString()"
          variant="info"
          size="small"
          class="notification-toggle__badge"
        />
      </div>
    </Button>
    
    <!-- Teleport overlay + panel to body to avoid clipping by status bar overflow/stacking contexts -->
    <teleport to="body">
      <div
        v-if="isOpen"
        class="notification-toggle__overlay"
        @click="closePanel"
      />
      <Transition
        name="notification-panel"
        appear
      >
        <div
          v-if="isOpen"
          ref="panelRef"
          class="notification-toggle__panel"
          role="dialog"
          aria-label="Notification history"
          @keydown.escape="closePanel"
        >
          <NotificationHistoryPanel
            :visible="isOpen"
            :notifications="notifications"
            @close="closePanel"
            @notification-action="handleNotificationAction"
            @clear-all="clearHistory"
            @remove-from-history="handleRemoveFromHistory"
            @expand="handleExpand"
            @collapse="handleCollapse"
          />
        </div>
      </Transition>
    </teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import Button from '@/components/atoms/Button.vue';
import Badge from '@/components/atoms/Badge.vue';
import Icon from '@/components/atoms/Icon.vue';
import NotificationHistoryPanel from '@/components/organisms/NotificationHistoryPanel.vue';
import { useNotifications } from '@/composables/useNotifications';

// Props: optional external count (e.g., unread count)
const props = defineProps<{ count?: number }>();

// Composables
const { 
  notifications, // full notification list (single source of truth)
  clear: clearAllNotifications,
  closeFromHistory,
  handleExpand: expandNotification,
  handleCollapse: collapseNotification,
  historyNotificationCount,
  openPanel,
  closePanel: closePanelState,
  panelOpen
} = useNotifications();

// State
const panelRef = ref<HTMLElement>();

// Computed
const notificationCount = computed(() => props.count ?? historyNotificationCount.value);
const isOpen = computed(() => panelOpen.value);

// Methods
const togglePanel = () => {
  if (panelOpen.value) {
    closePanel();
  } else {
    openPanel();
  }
};

const closePanel = () => {
  closePanelState();
};

const clearHistory = () => {
  clearAllNotifications();
  closePanel();
};

const handleRemoveFromHistory = (id: string) => {
  closeFromHistory(id);
};

const handleExpand = (id: string) => {
  expandNotification(id);
};

const handleCollapse = (id: string) => {
  collapseNotification(id);
};

const handleNotificationAction = (notificationId: string, actionId: string) => {
  // Forward to notification composable's action handler
  const notification = notifications.value.find((n: any) => n.id === notificationId);
  if (notification?.onAction) {
    notification.onAction(actionId);
  }
};
</script>

<style scoped lang="scss">
.notification-toggle {
  position: relative;
  display: inline-block;

  &__button {
    position: relative;
    // Remove custom styling, let StatusBar handle it
  }

  &__icon-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__badge {
    position: absolute;
    top: -4px;
    right: -6px;
    z-index: 1;
    pointer-events: none;
    
    // Custom styling for status bar context
    padding: 0 3px 2px 3px;
    font-size: 10px;
    min-height: min-content;
    line-height: 1;
    border-radius: 5px;
    
    // Ensure it stays within status bar bounds
    max-width: max-content;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 5000; // Ensure notification overlay is always the topmost layer
    background: transparent;
  }

  &__panel {
    /* Fixed + teleported so it is not clipped by any ancestor overflow */
    position: fixed;
    right: 8px;
    bottom: calc(var(--status-bar-height, 24px) + 8px);
    z-index: 5001; // Above overlay content
    width: 400px;
    max-width: 90vw;
    max-height: calc(100vh - 60px - var(--status-bar-height, 24px) - 16px);
    pointer-events: all;
    /* NotificationHistoryPanel supplies surface styles */

    @media (max-width: 768px) {
      left: 8px;
      right: 8px;
      width: auto;
      top: 60px;
      bottom: calc(var(--status-bar-height, 24px) + 30px);
      max-width: none;
      max-height: none; // Use top/bottom constraints instead
    }
  }
}

// Panel transition animations
.notification-panel-enter-active,
.notification-panel-leave-active {
  transition: all 0.2s ease;
}

.notification-panel-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.notification-panel-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.notification-panel-enter-to,
.notification-panel-leave-from {
  opacity: 1;
  transform: translateY(0) scale(1);
}

// Responsive adjustments
@media (max-width: 480px) {
  .notification-toggle {
    &__panel {
      top: 56px;
      bottom: 26px; // Leave space for status bar on smaller screens
      left: 4px;
      right: 4px;
    }
  }
}

// High contrast mode support
@media (prefers-contrast: high) {
  .notification-toggle {
    &__button {
      border: 1px solid var(--vscode-contrastBorder);
    }
    /* Removed panel border - let NotificationHistoryPanel handle its own high contrast styling */
  }
}

// Reduced motion support
@media (prefers-reduced-motion: reduce) {
  .notification-panel-enter-active,
  .notification-panel-leave-active {
    transition: opacity 0.2s ease;
  }

  .notification-panel-enter-from,
  .notification-panel-leave-to {
    transform: none;
  }
}
</style>
